import { VehiclePropsSchema, FundraisingOpportunityPropsSchema } from '../../graph-schemas/pevc-fundraising';
import type { NodeDefinition } from './base-crm';

export const PEVC_FUNDRAISING_NODES: Record<string, NodeDefinition> = {
  Vehicle: {
    type: 'Vehicle',
    schema: VehiclePropsSchema,
    displayName: 'Vehicle',
    displayNamePlural: 'Vehicles',
    icon: 'Landmark',
    color: '#8b5cf6',
    description: 'Investment vehicles (funds, SPVs, sidecars) that you manage and raise capital for',
    synonyms: ['fund', 'investment fund', 'venture fund', 'PE fund', 'VC fund', 'SPV', 'sidecar'],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', required: true, picklistName: 'vehicle_type' },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'vehicle_status' },
      { key: 'vintage_year', label: 'Vintage Year', type: 'number', format: 'year' },
      { key: 'target_size', label: 'Target Size', type: 'number', format: 'currency' },
    ],
    pmnEnabled: true,
    relationshipSections: [
      {
        sectionLabel: 'Deals',
        icon: 'Handshake',
        edgeTypes: ['INVESTING_FROM'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Deal', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'stage', headerName: 'Stage', width: 120, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Investments',
        icon: 'TrendingUp',
        edgeTypes: ['INVESTMENT_FROM'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Investment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'invested_amount', headerName: 'Amount', width: 120, type: 'number', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Fundraising Opportunities',
        icon: 'HandCoins',
        edgeTypes: ['OPPORTUNITY_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Opportunity', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'stage', headerName: 'Stage', width: 120, source: 'node', editable: false },
          { field: 'target_amount', headerName: 'Target', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Commitments',
        icon: 'FileCheck',
        edgeTypes: ['COMMITMENT_TO'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'commitment_amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Capital Calls',
        icon: 'ArrowUpCircle',
        edgeTypes: ['CALL_FROM'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Call', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'call_amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
          { field: 'call_date', headerName: 'Date', width: 120, type: 'date', source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Distributions',
        icon: 'ArrowDownCircle',
        edgeTypes: ['DISTRIBUTION_FROM'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Distribution', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
          { field: 'distribution_date', headerName: 'Date', width: 120, type: 'date', source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
    ],
  },
  FundraisingOpportunity: {
    type: 'FundraisingOpportunity',
    schema: FundraisingOpportunityPropsSchema,
    displayName: 'Fundraising Opportunity',
    displayNamePlural: 'Fundraising Opportunities',
    icon: 'HandCoins',
    color: '#ec4899',
    description: 'Potential LPs (limited partners) you are pitching to raise capital for your fund',
    synonyms: ['LP prospect', 'LP opportunity', 'fundraising prospect', 'potential LP', 'investor prospect'],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'stage', label: 'Stage', type: 'select', required: true, picklistName: 'fundraising_stage' },
      { key: 'target_amount', label: 'Target Amount', type: 'number', format: 'currency' },
      { key: 'committed_amount', label: 'Committed Amount', type: 'number', format: 'currency' },
      { key: 'expected_close_date', label: 'Expected Close', type: 'date' },
      { key: 'source', label: 'Source', type: 'select', picklistName: 'fundraising_source' },
      { key: 'owner_user_id', label: 'Owner', type: 'user' },
    ],
    pmnEnabled: true,
    relationshipSections: [
      {
        sectionLabel: 'Vehicle',
        icon: 'Landmark',
        edgeTypes: ['OPPORTUNITY_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Vehicle', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
    ],
  },
};
